import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { Flag, Trophy, Zap, Loader2, Gauge, Send } from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { getSocket } from '../lib/socket';
import { themeOf, ARENA } from '../lib/classTheme';
import { ArenaFloor, ArenaStatBar, Laurel, GoldFrame } from '../components/ArenaFrame';
import { SkeletonList } from '../components/ui';

const SEG_ICON = { straight: '🛣️', curve: '↩️', chicane: '⚡', jump: '🚀' };

export default function RaceRoom() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();

  const [state, setState] = useState(null);
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [floats, setFloats] = useState([]);
  const logRef = useRef();

  const { data: meta } = useQuery({ queryKey: ['raceMeta'], queryFn: () => api.get('/races/meta').then(r => r.data) });

  const pushFloat = useCallback((f) => {
    const item = { ...f, id: Math.random() };
    setFloats(v => [...v, item]);
    setTimeout(() => setFloats(v => v.filter(x => x.id !== item.id)), 1600);
  }, []);

  useEffect(() => {
    api.get(`/races/${id}`).then(r => setState(r.data))
      .catch(e => { toast.error(e.message); nav('/race'); });

    const s = getSocket(); if (!s) return;
    s.emit('race:join', id);
    s.emit('join', `race:${id}`);

    const onUpdate = (d) => {
      setState(prev => ({ ...d, isP1: prev?.isP1 ?? d.isP1 }));
      if (d.lastSegment) {
        const mine = (prev => prev)(null);
        const p = d.lastSegment.p1, q2 = d.lastSegment.p2;
        [p, q2].forEach((x, i) => {
          if (x?.spun) pushFloat({ type: 'spin', text: '💥 SPIN!', side: i === 0 ? 'left' : 'right' });
          else if (x?.distance) pushFloat({ type: 'dist', text: `+${x.distance}m`, side: i === 0 ? 'left' : 'right' });
        });
      }
      if (d.finished) setResult(d.rewards ?? { winnerId: d.winnerId });
    };
    const onFinish = ({ winnerId, rewards }) => {
      setResult({ ...rewards, winnerId });
      qc.invalidateQueries(['car', 'hero']);
    };

    s.on('race:update', onUpdate);
    s.on('race:finished', onFinish);
    return () => { s.off('race:update', onUpdate); s.off('race:finished', onFinish); s.emit('leave', `race:${id}`); };
  }, [id, nav, qc, pushFloat]);

  useEffect(() => { logRef.current?.scrollTo({ top: 9e9, behavior: 'smooth' }); }, [state?.log?.length]);

  async function submit() {
    if (!action || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/races/${id}/action`, { action });
      setState(data);
      if (data.finished) setResult(data.rewards ?? { winnerId: data.winnerId });
      setAction(null);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  if (!state || !meta) return <SkeletonList n={4} h="h-40" />;

  const me = state.isP1 ? state.p1 : state.p2;
  const opp = state.isP1 ? state.p2 : state.p1;
  const t = themeOf(me.classKey);
  const over = state.state === 'finished' || result;
  const seg = state.currentSegment;
  const totalDist = Math.max(me.distance, opp.distance, 1);

  return (
    <div className="max-w-lg mx-auto space-y-3 animate-fade-up">
      {/* ═══ TRACK ═══ */}
      <div className="card-arena overflow-hidden">
        <ArenaFloor classKey={me.classKey} className="p-4" showCrowd>
          {/* floats */}
          <AnimatePresence>
            {floats.map(f => (
              <motion.div key={f.id}
                initial={{ opacity: 0, y: 16, scale: .6 }}
                animate={{ opacity: [0, 1, 1, 0], y: [-4, -50, -70], scale: [.6, 1.2, 1] }}
                exit={{ opacity: 0 }} transition={{ duration: 1.3 }}
                className={`absolute top-20 z-30 font-display font-black text-lg pointer-events-none
                  ${f.side === 'left' ? 'left-[22%]' : 'right-[22%]'}`}
                style={{ color: f.type === 'spin' ? '#ef4444' : ARENA.goldL }}>
                {f.text}
              </motion.div>
            ))}
          </AnimatePresence>

          {/* header */}
          <div className="flex items-center justify-between text-[10px] font-display font-black mb-3"
            style={{ color: ARENA.goldL }}>
            <span className="px-2 py-0.5 rounded-lg" style={{ background: 'rgba(232,200,116,.14)' }}>
              {state.track.name}
            </span>
            {state.stake > 0 && (
              <span className="px-2 py-0.5 rounded-lg" style={{ background: 'rgba(232,200,116,.2)' }}>
                💎 {state.stake * 2}
              </span>
            )}
            <span className="px-2 py-0.5 rounded-lg" style={{ background: 'rgba(232,200,116,.14)' }}>
              {state.segmentIndex}/{state.totalSegments}
            </span>
          </div>

          {/* race track visual */}
          <div className="relative py-3">
            {/* lane 1 */}
            <RaceLane racer={me} isMe pct={(me.distance / totalDist) * 100} theme={t} />
            <div className="h-2" />
            <RaceLane racer={opp} pct={(opp.distance / totalDist) * 100} theme={themeOf(opp.classKey)} />

            {/* finish line */}
            <div className="absolute right-1 inset-y-0 w-1.5"
              style={{
                background: 'repeating-linear-gradient(0deg,#fff 0 6px,#241e16 6px 12px)',
                opacity: .8,
              }} />
          </div>

          {/* current segment */}
          {seg && !over && (
            <motion.div key={seg.index} initial={{ scale: .9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="mt-3 rounded-xl p-3 text-center"
              style={{ background: 'rgba(232,200,116,.12)', border: '1px solid rgba(232,200,116,.3)' }}>
              <div className="flex items-center justify-center gap-2">
                <span className="text-2xl">{SEG_ICON[seg.type]}</span>
                <div className="text-left">
                  <p className="font-display font-black text-sm" style={{ color: ARENA.goldL }}>
                    {seg.label} · Lap {seg.lap}
                  </p>
                  <p className="text-[10px]" style={{ color: 'rgba(232,220,200,.65)' }}>{seg.hint}</p>
                </div>
              </div>
              {state.nextSegments?.length > 0 && (
                <div className="flex items-center justify-center gap-1 mt-2">
                  <span className="text-[9px] font-bold" style={{ color: 'rgba(232,220,200,.5)' }}>NEXT:</span>
                  {state.nextSegments.map((n, i) => (
                    <span key={i} className="text-sm opacity-60">{SEG_ICON[n.type]}</span>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </ArenaFloor>

        {/* log */}
        <div ref={logRef} className="h-24 overflow-y-auto px-3 py-2 text-[11px] font-mono space-y-0.5"
          style={{ background: '#1a1410', color: 'rgba(232,220,200,.8)' }}>
          {state.log?.map((l, i) => (
            <motion.p key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
              className={l.text?.includes('SPUN') ? 'text-red-400 font-bold'
                : l.text?.includes('nails') ? 'text-amber-300' : ''}>
              {l.text}
            </motion.p>
          ))}
        </div>
      </div>

      {/* ═══ RESULT ═══ */}
      {over && result && (
        <motion.div initial={{ scale: .85, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 220, damping: 18 }}
          className="card-arena p-6 text-center relative overflow-hidden">
          {result.winnerId === user.id && (
            <motion.div className="absolute inset-0 opacity-25"
              style={{ background: 'conic-gradient(from 0deg,transparent,rgba(232,200,116,.6),transparent)' }}
              animate={{ rotate: 360 }} transition={{ duration: 4, repeat: Infinity, ease: 'linear' }} />
          )}
          <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 1.4, repeat: 2 }}>
            <Trophy size={42} className="mx-auto mb-2 relative" style={{ color: ARENA.goldL }} />
          </motion.div>
          <h2 className="font-display text-3xl font-extrabold relative"
            style={{ color: ARENA.goldL, textShadow: '0 2px 18px rgba(201,151,58,.5)' }}>
            {!result.winnerId ? 'DRAW' : result.winnerId === user.id ? 'VICTORY!' : 'DEFEAT'}
          </h2>

          <div className="mt-3 space-y-1 relative text-white">
            <p className="font-display font-bold text-sm">
              {(state.isP1 ? result.p1TimeMs : result.p2TimeMs) / 1000}s
              {' vs '}
              {(state.isP1 ? result.p2TimeMs : result.p1TimeMs) / 1000}s
            </p>
            {result.winnerPoints !== undefined && (
              <p className="text-sm opacity-90">
                {result.winnerId === user.id
                  ? `+${result.winnerPoints} 💎 · +${result.winnerXp} XP`
                  : `+${result.loserPoints} 💎 · +${result.loserXp} XP`}
              </p>
            )}
            <p className="text-xs opacity-75">
              ELO {state.isP1 ? result.p1EloDelta : result.p2EloDelta > 0 ? '+' : ''}
              {state.isP1 ? result.p1EloDelta : result.p2EloDelta}
            </p>
          </div>

          <div className="flex gap-2 mt-5 relative">
            <Link to="/race" className="btn-ghost flex-1">Circus</Link>
            <Link to="/garage" className="btn-primary flex-1">Tune up</Link>
          </div>
        </motion.div>
      )}

      {/* ═══ CONTROLS ═══ */}
      {!over && (
        <>
          <div className="card p-3">
            <p className="label">Choose your line</p>
            <div className="grid grid-cols-3 gap-1.5">
              {state.actions?.map(a => {
                const isBest = seg?.best?.includes(a.key);
                const isWorst = seg?.worst?.includes(a.key);
                const sel = action === a.key;
                return (
                  <motion.button key={a.key} onClick={() => setAction(a.key)} disabled={a.disabled}
                    whileTap={{ scale: .93 }}
                    className={`relative p-2 rounded-xl border-2 text-center transition
                      ${a.disabled ? 'opacity-40' : ''}`}
                    style={sel
                      ? { borderColor: ARENA.gold, background: 'rgba(201,151,58,.12)' }
                      : isBest ? { borderColor: '#27ae60', background: 'rgba(39,174,96,.06)' }
                      : isWorst ? { borderColor: '#c0392b', background: 'rgba(192,57,43,.05)' }
                      : { borderColor: 'var(--stone-200)', background: 'var(--marble)' }}>
                    <div className="text-xl">{a.icon}</div>
                    <p className="text-[10px] font-display font-black mt-0.5">{a.name}</p>
                    <p className="text-[8px] font-bold" style={{ color: 'var(--stone-400)' }}>
                      {a.nitroCost > 0 ? `💨${a.nitroCost}` : a.nitroGain > 0 ? `+${a.nitroGain}💨` : ''}
                    </p>
                    {isBest && <span className="absolute top-1 right-1 text-[9px]">✨</span>}
                    {isWorst && <span className="absolute top-1 right-1 text-[9px]">⚠️</span>}
                  </motion.button>
                );
              })}
            </div>
            {action && (
              <p className="text-[11px] mt-2 font-semibold" style={{ color: 'var(--stone-500)' }}>
                {meta.actions[action]?.desc}
              </p>
            )}
          </div>

          <motion.button onClick={submit} disabled={!action || busy || state.myPending}
            whileTap={{ scale: .96 }}
            className="w-full py-4 rounded-2xl font-display text-base font-black text-white
              transition disabled:opacity-50"
            style={{ background: ARENA.goldFrame, boxShadow: '0 6px 24px -6px rgba(201,151,58,.6)' }}>
            {busy ? <><Loader2 size={16} className="animate-spin inline mr-2" /> Racing…</>
              : state.myPending ? '⏳ Waiting for opponent'
              : !action ? 'Pick your line'
              : `🏁 ${meta.actions[action]?.name.toUpperCase()}`}
          </motion.button>

          <button onClick={() => confirm('Forfeit this race?')
            && api.post(`/races/${id}/forfeit`).then(() => nav('/race'))}
            className="w-full text-xs font-bold py-2 flex items-center justify-center gap-1"
            style={{ color: 'var(--stone-400)' }}>
            <Flag size={12} /> Forfeit
          </button>
        </>
      )}
    </div>
  );
}

/* ---------------- RACE LANE ---------------- */
function RaceLane({ racer, isMe, pct, theme }) {
  return (
    <div className="relative">
      {/* road */}
      <div className="relative h-11 rounded-lg overflow-hidden"
        style={{
          background: 'linear-gradient(180deg,#3d3325,#5c4d38)',
          border: `1px solid ${isMe ? theme.hex + '66' : 'rgba(232,200,116,.15)'}`,
        }}>
        {/* dashes */}
        <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2"
          style={{
            background: 'repeating-linear-gradient(90deg,rgba(232,220,200,.35) 0 12px,transparent 12px 26px)',
          }} />

        {/* vehicle */}
        <motion.div className="absolute top-1/2 -translate-y-1/2 flex items-center gap-1.5"
          animate={{ left: `${Math.min(88, pct)}%` }}
          transition={{ type: 'spring', stiffness: 60, damping: 16 }}>
          <div className="w-11 h-8 rounded overflow-hidden shrink-0"
            style={{
              padding: 1.5,
              background: isMe ? ARENA.goldFrame : 'rgba(232,200,116,.35)',
              boxShadow: isMe ? `0 0 14px -2px ${theme.hex}` : 'none',
            }}>
            <div className="w-full h-full rounded-sm overflow-hidden bg-stone-900">
              <img src={racer.image} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
          {/* speed trail */}
          <motion.div className="h-0.5 rounded-full"
            style={{ background: `linear-gradient(90deg,${theme.hex},transparent)`, width: 18 }}
            animate={{ opacity: [.3, .9, .3], scaleX: [.7, 1.2, .7] }}
            transition={{ duration: .5, repeat: Infinity }} />
        </motion.div>
      </div>

      {/* info bar */}
      <div className="flex items-center gap-2 mt-1">
        <p className="text-[10px] font-display font-bold truncate flex-1"
          style={{ color: isMe ? ARENA.goldL : 'rgba(232,220,200,.65)' }}>
          {isMe && '▸ '}{racer.carName} · {racer.username}
        </p>
        <div className="flex gap-1.5 shrink-0">
          <Gauge2 label="💨" value={racer.nitro} color="#8e44ad" />
          <Gauge2 label="🛞" value={racer.tires} color="#27ae60" />
          {racer.blocks > 0 && <span className="text-[9px]">🚧</span>}
        </div>
      </div>
    </div>
  );
}

function Gauge2({ label, value, color }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[9px]">{label}</span>
      <div className="w-8 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,.4)' }}>
        <motion.div className="h-full rounded-full"
          style={{ background: color }} animate={{ width: `${value}%` }} />
      </div>
    </div>
  );
}